import { useCallback, useEffect, useRef, useState } from 'react';
import {
  addStructuredRow,
  deleteStructuredRow,
  editStructuredField,
  getContractStructuredData,
  runContractSchema,
  updateStructuredRow,
  verifyStructuredOutput,
} from './structuredDataApi';

const POLL_MS = 3000;

// Data + polling for a contract's Structured Data tab. Fetches once on mount,
// then polls while any run is pending so a finished run swaps in place without
// a page reload. Returns null-ish shape until the first load resolves.
export default function useStructuredData(contractId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [runningIds, setRunningIds] = useState(() => new Set());
  const alive = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await getContractStructuredData(contractId);
      if (!alive.current) return null;
      setData(res);
      setError(null);
      return res;
    } catch (err) {
      if (alive.current) setError(err.message);
      return null;
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    alive.current = true;
    setLoading(true);
    setData(null);
    refresh();
    return () => { alive.current = false; };
  }, [refresh]);

  const anyPending = !!data?.outputs?.some((o) => o.status === 'pending');

  useEffect(() => {
    if (!anyPending) return undefined;
    const timer = setInterval(refresh, POLL_MS);
    return () => clearInterval(timer);
  }, [anyPending, refresh]);

  const run = useCallback(async (schemaId, opts) => {
    setRunningIds((prev) => new Set(prev).add(schemaId));
    try {
      await runContractSchema(contractId, schemaId, opts);
      await refresh();
    } finally {
      setRunningIds((prev) => {
        const next = new Set(prev);
        next.delete(schemaId);
        return next;
      });
    }
  }, [contractId, refresh]);

  // Phase b: per-field edit-in-place and Mark verified. Both re-read the tab
  // payload afterwards so chips, locks, and provenance stay consistent.
  const editField = useCallback(async (schemaId, fieldKey, value) => {
    await editStructuredField(contractId, schemaId, fieldKey, value);
    await refresh();
  }, [contractId, refresh]);

  const verify = useCallback(async (schemaId) => {
    await verifyStructuredOutput(contractId, schemaId);
    await refresh();
  }, [contractId, refresh]);

  // Row-level ops on array fields (doc-46 mechanism 3).
  const addRow = useCallback(async (schemaId, fieldKey, row) => {
    await addStructuredRow(contractId, schemaId, fieldKey, row);
    await refresh();
  }, [contractId, refresh]);

  const updateRow = useCallback(async (schemaId, fieldKey, index, row) => {
    await updateStructuredRow(contractId, schemaId, fieldKey, index, row);
    await refresh();
  }, [contractId, refresh]);

  const deleteRow = useCallback(async (schemaId, fieldKey, index) => {
    await deleteStructuredRow(contractId, schemaId, fieldKey, index);
    await refresh();
  }, [contractId, refresh]);

  return { data, loading, error, run, editField, addRow, updateRow, deleteRow, verify, runningIds, refresh };
}
